import { credentialTestBody, type CredentialTestResult } from './awsSso'
import { request, streamSSE } from './core'
import type {
  AwsConnectionOutput,
  AwsConnectionPayload,
  AwsInventoryResponse,
  AwsPlatformIdentity,
  PendingFinding,
} from './types'

export const awsImportApi = {
  // --- connection CRUD ---

  getAwsConnection: (slug: string) =>
    request<{ connection: AwsConnectionOutput | null }>(
      `/api/assessments/${slug}/aws-import/connection`,
    ),

  saveAwsConnection: (slug: string, payload: AwsConnectionPayload) =>
    request<AwsConnectionOutput>(
      `/api/assessments/${slug}/aws-import/connection`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),

  /** Either/or: keys present → tests exactly what was entered (pre-save);
   *  keys blank → the server decrypts and tests the SAVED credentials
   *  (`awsAccountId` picks the account row in per-account mode). */
  testAwsCredentials: (
    slug: string,
    accessKey: string,
    secretKey: string,
    expectedAccountId?: string,
    sessionToken?: string,
    awsAccountId?: string,
  ) =>
    request<CredentialTestResult>(`/api/assessments/${slug}/aws-import/connection/test`, {
      method: 'POST',
      body: JSON.stringify(
        credentialTestBody(accessKey, secretKey, expectedAccountId, sessionToken, awsAccountId),
      ),
    }),

  /** assume_role mode: performs the AssumeRole itself, proving the customer's
   *  trust policy admits us. No external id: the server always assumes with
   *  the company's own. */
  testAwsAssumeRole: (slug: string, roleArn: string, expectedAccountId?: string) =>
    request<CredentialTestResult>(`/api/assessments/${slug}/aws-import/connection/test`, {
      method: 'POST',
      body: JSON.stringify({ role_arn: roleArn, expected_account_id: expectedAccountId }),
    }),

  /** What the customer's role trust policy must name: the platform's own AWS
   *  account (same for every company) and THIS company's ExternalId. */
  getAwsPlatformIdentity: (slug: string) =>
    request<AwsPlatformIdentity>(`/api/assessments/${slug}/aws-import/identity`),

  // --- import lifecycle ---

  /** The stored inventory from the most recent scan; `snapshot` is null when
   *  never scanned. */
  getAwsInventory: (slug: string) =>
    request<AwsInventoryResponse>(`/api/companies/${slug}/aws-import/inventory`),

  getAwsImport: (slug: string) =>
    request<{
      status: 'none' | 'running' | 'done' | 'failed' | 'skipped'
      error?: string | null
      started_at?: string | null
      export_id?: string | null
      findings: PendingFinding[]
      revision: number
    }>(`/api/assessments/${slug}/aws-import`),

  streamAwsImport: (slug: string, onEvent: (evt: Record<string, unknown>) => void) =>
    streamSSE(`/api/assessments/${slug}/aws-import/run`, onEvent),

  applyAwsFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ applied: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/aws-import/findings/apply`,
      { method: 'POST', body: JSON.stringify({ field_paths: fieldPaths }) },
      { ifMatch: revision },
    ),

  dismissAwsFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ dismissed: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/aws-import/findings/dismiss`,
      { method: 'POST', body: JSON.stringify({ field_paths: fieldPaths }) },
      { ifMatch: revision },
    ),

  /** Company-level "skip for now": marks the COMPANY's
   *  aws_import_status='skipped'. */
  skipCompanyAwsOnboarding: (slug: string) =>
    request<{ ok: boolean; aws_import_status: string }>(
      `/api/companies/${slug}/aws-import/skip`,
      { method: 'POST' },
    ),
}
